/**
 * LEVANTAMIENTO DE FABRICACIÓN EN MADERA — la guía, los módulos y las tres lecturas.
 * =============================================================================
 *
 * Sexto y último rubro del plan. Sin plano, como iluminación: lo que importa aquí es el
 * hueco y cómo se reparte, no dónde queda cada cosa.
 *
 * ---------------------------------------------------------------------------
 * LO QUE ESTA PANTALLA TIENE QUE SOSTENER, Y ES DISTINTO DE LOS OTROS RUBROS
 *
 * **1. Que el error de medición se atrape aquí y no en la instalación.** En eléctrico un
 * error se corrige cambiando un cable. Aquí la placa ya se cortó: un mueble que no entra
 * se vuelve a fabricar, y lo paga el taller. Por eso el hueco se captura en tres medidas y
 * la pantalla compara contra la MENOR, no contra la que se midió primero.
 *
 * **2. Que el resultado hable los tres idiomas a la vez.** Módulos para quien levanta,
 * placas y minutos de máquina para el taller, metro lineal y lista de alcance para el
 * cliente. Es lo que Juan pidió el 2026-08-15, y resultó ser lo que el oficio ya sabía:
 * ninguna de las tres lecturas sola alcanza para cerrar una venta y fabricarla bien.
 *
 * **3. Que la lista de qué NO incluye esté a la vista.** No es letra chica: es lo que
 * gana la comparación contra un presupuesto más barato que trae menos cosas.
 */

import { useState, useMemo } from 'react'
import PasoGuia, { Avance } from './PasoGuia.jsx'
import { GUIA_TALLER } from '../motor/guia.js'
import {
  MODULOS, moduloPorId, calcularTaller, TALLER_BASE, ALCANCE_METRO_LINEAL,
  HOLGURA_HUECO, ANIDADO_PAGA_EN_VOLUMEN
} from '../motor/taller.js'

const mx = n => '$' + Math.round(n || 0).toLocaleString('es-MX')
const nuevoId = () => Math.random().toString(36).slice(2, 8)

const MODO_POR_PASO = { uso: 'ver', medir: 'medir', escuadra: 'medir', modulos: 'modulos', calcular: 'ver' }

export default function Taller ({ atras, onGuardar, taller = TALLER_BASE }) {
  const [i, setI] = useState(0)
  const [modulos, setModulos] = useState([])
  const [abierto, setAbierto] = useState(null)

  // El hueco, en tres alturas. Milímetros, que es como se mide en carpintería.
  const [anchoArriba, setAnchoArriba] = useState('')
  const [anchoMedio, setAnchoMedio] = useState('')
  const [anchoAbajo, setAnchoAbajo] = useState('')
  const [notas, setNotas] = useState('')
  const [hallazgos, setHallazgos] = useState('')

  const paso = GUIA_TALLER.pasos[i]

  const hueco = [anchoArriba, anchoMedio, anchoAbajo].map(v => parseFloat(v) || 0)

  const r = useMemo(() => {
    const listos = modulos.filter(m => m.ancho > 0)
    if (!listos.length) return null
    return calcularTaller({ modulos: listos, cfg: taller, hueco })
  }, [modulos, taller, anchoArriba, anchoMedio, anchoAbajo])

  const agregar = () => {
    const def = MODULOS[0]
    const id = nuevoId()
    setModulos([...modulos, {
      id, moduloId: def.id, ancho: def.ancho, alto: def.alto, fondo: def.fondo, cantidad: 1
    }])
    setAbierto(id)
  }
  const cambiar = (id, c) => setModulos(modulos.map(m => m.id === id ? { ...m, ...c } : m))
  const quitar = id => setModulos(modulos.filter(m => m.id !== id))

  const falta = (() => {
    if (paso.id === 'modulos' && !modulos.some(m => m.ancho > 0)) return 'Agrega al menos un módulo.'
    return null
  })()

  return (
    <div className="app">
      <div className="bar">
        <button className="ib ico" onClick={atras} aria-label="Regresar">
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <h1>Fabricación
          <span className="crumb">{GUIA_TALLER.duracion} · paso {i + 1} de {GUIA_TALLER.pasos.length}</span>
        </h1>
      </div>

      <main>
        <Avance pasos={GUIA_TALLER.pasos} i={i} onIr={setI} />
        <PasoGuia key={paso.id} paso={paso} />

        {paso.id === 'uso' && (
          <div className="captura">
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Qué guarda hoy y qué le falta acomodar</span>
              <textarea rows="3" value={notas} onChange={e => setNotas(e.target.value)}
                placeholder="Ej. No le caben las ollas grandes. Quiere el garrafón adentro. Usa la licuadora a diario y hoy vive en la barra. Va refrigerador empotrado, marca y modelo pendientes." />
            </label>
          </div>
        )}

        {paso.id === 'medir' && (
          <div className="captura">
            <div className="sec" style={{ margin: '0 0 var(--e3)' }}>
              Ancho del hueco, en milímetros
            </div>
            {/* Tres medidas, no una. Es lo que separa un mueble que entra de uno que se
                vuelve a fabricar, y el sistema compara contra la MENOR. */}
            <label className="f">
              <span>Arriba</span>
              <input type="number" inputMode="numeric" value={anchoArriba}
                onChange={e => setAnchoArriba(e.target.value)} placeholder="Ej. 2405" />
            </label>
            <label className="f">
              <span>A la mitad</span>
              <input type="number" inputMode="numeric" value={anchoMedio}
                onChange={e => setAnchoMedio(e.target.value)} placeholder="Ej. 2398" />
            </label>
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Abajo</span>
              <input type="number" inputMode="numeric" value={anchoAbajo}
                onChange={e => setAnchoAbajo(e.target.value)} placeholder="Ej. 2392" />
              <span>Los tres. Ningún muro es recto, y la diferencia entre arriba y abajo
                    suele ser de uno o dos centímetros.</span>
            </label>

            {hueco.filter(h => h > 0).length > 1 && (
              <div className="nota acento" style={{ marginTop: 'var(--e4)' }}>
                El muro varía <b>{Math.max(...hueco.filter(h => h > 0)) - Math.min(...hueco.filter(h => h > 0))} mm</b>{' '}
                entre su punto más ancho y el más angosto. Se va a diseñar contra{' '}
                <b>{Math.min(...hueco.filter(h => h > 0))} mm</b>, que es el que tiene que pasar.
              </div>
            )}
          </div>
        )}

        {paso.id === 'escuadra' && (
          <div className="captura">
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Desnivel, desplome y lo que estorba</span>
              <textarea rows="3" value={hallazgos} onChange={e => setHallazgos(e.target.value)}
                placeholder="Ej. El piso cae 8 mm hacia la ventana. La esquina izquierda no está a escuadra. Hay toma de gas a 40 cm de la esquina, dentro del hueco. Contacto a 110 cm." />
              <span>Todo esto se resuelve, pero se cotiza. Descubrirlo el día de la
                    instalación convierte una entrega en un reclamo.</span>
            </label>
          </div>
        )}

        {paso.id === 'modulos' && (
          <div className="captura">
            <div className="sec" style={{ margin: '0 0 var(--e3)' }}>
              Módulos ({modulos.length})
            </div>

            {modulos.map(m => {
              const def = moduloPorId(m.moduloId)
              const esteAbierto = abierto === m.id
              return (
                <div className="card" key={m.id}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <button type="button" className="grow" style={{
                      background: 'none', border: 0, textAlign: 'left', cursor: 'pointer', padding: 0
                    }} onClick={() => setAbierto(esteAbierto ? null : m.id)}>
                      <div className="t">{def.nombre}</div>
                      <div className="s">
                        {m.ancho} × {m.alto} × {m.fondo} mm
                        {m.cantidad > 1 && ` · ${m.cantidad} piezas`}
                      </div>
                    </button>
                    <button className="ib sm dz" aria-label={`Quitar ${def.nombre}`}
                      onClick={() => quitar(m.id)}>×</button>
                  </div>

                  {esteAbierto && (
                    <div style={{ marginTop: 'var(--e4)' }}>
                      <label className="f">
                        <span>Qué módulo es</span>
                        <select value={m.moduloId} onChange={ev => {
                          const d = moduloPorId(ev.target.value)
                          cambiar(m.id, { moduloId: d.id, ancho: d.ancho, alto: d.alto, fondo: d.fondo })
                        }}>
                          {MODULOS.map(x => <option key={x.id} value={x.id}>{x.nombre}</option>)}
                        </select>
                        <span style={{ marginTop: 4 }}>{def.que}</span>
                        <span>{def.paraElCliente}</span>
                      </label>

                      <div className="two">
                        <label className="f">
                          <span>Ancho, en mm</span>
                          <input type="number" inputMode="numeric" value={m.ancho}
                            onChange={ev => cambiar(m.id, { ancho: parseInt(ev.target.value) || 0 })} />
                        </label>
                        <label className="f">
                          <span>Piezas iguales</span>
                          <input type="number" inputMode="numeric" value={m.cantidad}
                            onChange={ev => cambiar(m.id, { cantidad: Math.max(1, parseInt(ev.target.value) || 1) })} />
                        </label>
                      </div>
                      <div className="two">
                        <label className="f">
                          <span>Alto, en mm</span>
                          <input type="number" inputMode="numeric" value={m.alto}
                            onChange={ev => cambiar(m.id, { alto: parseInt(ev.target.value) || 0 })} />
                        </label>
                        <label className="f">
                          <span>Fondo, en mm</span>
                          <input type="number" inputMode="numeric" value={m.fondo}
                            onChange={ev => cambiar(m.id, { fondo: parseInt(ev.target.value) || 0 })} />
                        </label>
                      </div>
                      {m.ancho > 900 && (
                        <div className="warn leible">
                          <b>Más de 90 cm de ancho.</b> Una caja así se pandea con el tiempo,
                          desperdicia placa y no la carga una persona. Conviene partirla en dos.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}

            <button className="ib pri w" onClick={agregar} style={{ marginTop: 'var(--e3)' }}>
              Agregar módulo
            </button>

            {/* La comprobación que evita el error caro del rubro, EN VIVO mientras se
                arma el mueble — no al final, cuando ya se decidió todo. */}
            {r?.hueco?.hayHueco && (
              <div className={r.hueco.cabe ? 'nota acento' : 'warn leible'}
                style={{ marginTop: 'var(--e4)' }}>
                <b>{r.hueco.cabe ? 'Entra.' : 'No entra.'}</b> {r.hueco.porque.replace(/\*\*/g, '')}
              </div>
            )}
          </div>
        )}

        {paso.id === 'calcular' && (
          r ? <Resultado r={r} notas={notas} hallazgos={hallazgos} onGuardar={onGuardar} />
            : <div className="empty">
                <b>Todavía no se puede calcular.</b>
                <p>Agrega al menos un módulo con su ancho.</p>
              </div>
        )}
      </main>

      <div className="fab"><div className="in">
        <button className="gh" onClick={() => setI(Math.max(i - 1, 0))} disabled={i === 0}
          aria-label="Paso anterior">←</button>
        <button onClick={() => setI(Math.min(i + 1, GUIA_TALLER.pasos.length - 1))}
          disabled={!!falta || i === GUIA_TALLER.pasos.length - 1}>
          {falta || (i === GUIA_TALLER.pasos.length - 1 ? 'Listo' : 'Siguiente')}
        </button>
      </div></div>
    </div>
  )
}

function Resultado ({ r, notas, hallazgos, onGuardar }) {
  const [auditando, setAuditando] = useState(false)

  return (
    <>
      {/* ---- La comprobación del hueco va PRIMERO. Es el error que no se corrige. ---- */}
      {r.hueco.hayHueco && (
        <>
          <div className="sec">¿Entra en el hueco?</div>
          <div className={r.hueco.cabe ? 'nota leible' : 'warn leible'}>
            <b>{r.hueco.cabe ? 'Sí entra.' : 'NO ENTRA.'}</b>{' '}
            {r.hueco.porque.replace(/\*\*/g, '')}
            <p style={{ margin: 'var(--e3) 0 0' }}>{r.hueco.porqueDesplome}</p>
          </div>
          {r.hueco.unaSolaMedida && (
            <div className="warn leible" style={{ marginTop: 'var(--e3)' }}>
              Sólo se midió el hueco en un punto. <b>Regresa y mídelo arriba, a la mitad y
              abajo</b> antes de mandar a cortar: en este rubro el error de medición no se
              corrige, se vuelve a fabricar.
            </div>
          )}
        </>
      )}

      {/* ---- 1. La lectura de quien levanta: módulos ---- */}
      <div className="sec">Los módulos</div>
      <table className="d">
        <thead>
          <tr>
            <th>Módulo</th>
            <th className="n">Ancho</th>
            <th className="n">Piezas</th>
            <th className="n">Costo</th>
          </tr>
        </thead>
        <tbody>
          {r.detalle.map((d, n) => (
            <tr key={n}>
              <td>
                {d.modulo.nombre}
                {auditando && <div className="s">
                  {d.despiece.piezas.reduce((s, p) => s + p.cant, 0)} piezas cortadas ·{' '}
                  {d.fab.minutosMaquina} min de máquina · {d.fab.minutosTotal} min en total
                </div>}
              </td>
              <td className="n">{d.medidas.ancho}</td>
              <td className="n">{d.cantidad}</td>
              <td className="n">{mx(d.costo)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="3"><b>Costo de fabricación</b></td>
            <td className="n"><b>{mx(r.costoTotal)}</b></td>
          </tr>
        </tfoot>
      </table>

      {/* ---- 2. La lectura del cliente: metro lineal, con su advertencia ---- */}
      <div className="sec">Como lo va a comparar el cliente</div>
      <div className="ln big">
        <span>Costo por metro de frente</span>
        <b>{mx(r.costoPorMetro)}</b>
      </div>
      <div className="ln">
        <span>Frente total</span>
        <b>{r.metrosTotal.toFixed(2)} m</b>
      </div>
      <div className="warn leible" style={{ marginTop: 'var(--e4)' }}>
        {r.avisoMetro}
      </div>

      {/* La lista que gana la comparación. No es letra chica: es el argumento. */}
      <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
        <b>Qué incluye este precio:</b>
        <ul style={{ margin: 'var(--e2) 0 var(--e3)', paddingLeft: '1.1rem' }}>
          {ALCANCE_METRO_LINEAL.incluye.map((x, n) => <li key={n}>{x}</li>)}
        </ul>
        <b>Qué NO incluye, y se cotiza aparte:</b>
        <ul style={{ margin: 'var(--e2) 0 0', paddingLeft: '1.1rem' }}>
          {ALCANCE_METRO_LINEAL.noIncluye.map((x, n) => <li key={n}>{x}</li>)}
        </ul>
        <p style={{ margin: 'var(--e3) 0 0' }}>
          Si al cliente le pasan un presupuesto más barato, <b>comparen las dos listas antes
          que los dos precios</b>. Casi siempre la diferencia está aquí y no en el mueble.
        </p>
      </div>

      {/* ---- 3. La lectura del taller: placas y máquina ---- */}
      <div className="sec">Lo que hay que comprar y programar</div>
      {Object.entries(r.placas).map(([k, p]) => (
        <div className="ln" key={k}>
          <span>{p.nombre}</span>
          <b>{p.placas} {p.placas === 1 ? 'placa' : 'placas'}</b>
        </div>
      ))}
      <div className="ln">
        <span>Minutos de máquina
          <span className="s" style={{ display: 'block' }}>
            El recurso escaso del taller: es lo que hay que programar en la semana
          </span>
        </span>
        <b>{Math.round(r.detalle.reduce((s, d) => s + d.fab.minutosMaquina * d.cantidad, 0))} min</b>
      </div>

      <button type="button" className="discreto" style={{ marginTop: 'var(--e5)' }}
        onClick={() => setAuditando(!auditando)} aria-expanded={auditando}>
        {auditando ? 'Ocultar de dónde sale cada número' : 'Ver de dónde sale cada número'}
      </button>

      {auditando && (
        <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
          <p style={{ marginTop: 0 }}>
            <b>La placa se compra entera, no por metro cuadrado.</b> Lo que decide cuántas
            hacen falta no es el área de las piezas sino cuántas caben —el anidado—, así que
            el consumo se redondea hacia arriba a placas completas.
          </p>
          <p>
            <b>{ANIDADO_PAGA_EN_VOLUMEN}</b>
          </p>
          <p>
            <b>El corte se cobra por trayectoria, no por área.</b> Una pieza calada con
            mucho detalle lleva el mismo material que una lisa y cuesta el triple de
            máquina. Por eso los metros de corte salen del perímetro.
          </p>
          <p style={{ marginBottom: 0 }}>
            <b>Todo lo de arriba es COSTO</b>, sin utilidad ni IVA. Los márgenes los aplica
            el motor de precios encima, igual que a cualquier otro producto. Y los valores
            del taller —placa, minuto de máquina, herraje— se editan en{' '}
            <b>Configuración → Taller</b>.
          </p>
        </div>
      )}

      {(notas || hallazgos) && (
        <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
          {notas && <p style={{ marginTop: 0 }}><b>Del uso:</b> {notas}</p>}
          {hallazgos && <p style={{ marginBottom: 0 }}><b>Del muro:</b> {hallazgos}</p>}
        </div>
      )}

      {onGuardar && (
        <button className="ib pri w" style={{ marginTop: 'var(--e6)' }}
          onClick={() => onGuardar(r)}>Agregar a la cotización</button>
      )}
    </>
  )
}
